import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box as MuiBox,
  Container as MuiContainer,
  Stack as MuiStack,
  Typography,
  Button as MuiButton,
  IconButton as MuiIconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Table as MuiTable,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import muiTheme from "../../theme/mui/styles";
import VendorAccountsModal from "./vendorModals/vendorAccountsModal";
import VendorModal from "./vendorModals/vendorModal";
import PendingApprovalsModal from "./vendorModals/pendingApprovalModal";
import VendorConfirmationModal from "./vendorModals/vendorConfirmationModal";
import MergeVendorsModal from "./vendorModals/mergeVendorsModal";
import FilterVendorsModal from "./vendorModals/filterVendorsModal";
import VendorsExportModal from "./vendorModals/vendorsExportModal";
import { Context } from "../../ContextWrapper";
import { Download, Filter, GitMerge, CheckCircle } from "react-feather";
import { fetchAccounts } from "../../api/accounts";
import {
  createVendor,
  deleteVendor,
  fetchVendors,
  createVendorsExportLink,
  mergeVendors,
  updateVendor,
  approveVendor,
  unapproveVendor,
  fetchPendingVendorApprovals,
  searchVendorAccounts,
} from "../../api/vendors";
import { fetchCustomers } from "../../api/customers";
import { mapFrontendToBackend } from "../../utils/accountTypeUtils";

const VENDORS_PAGE_SIZE = 50;

const getEmptyFilters = () => ({
  customers: [],
  accountTypes: [],
  accounts: [],
  approvalStatus: [],
  includeAccountChildren: false,
  matchJournalAccounts: false,
});

const ACCOUNT_COPY_COLUMNS = [
  {
    key: "account_name",
    label: "Account Name",
    getValue: (client) => client.customer_name || "",
  },
  {
    key: "bookkeeper",
    label: "Bookkeeper",
    getValue: (client) => client.customer_bookkeeper || "",
  },
  {
    key: "account_id",
    label: "Account ID",
    getValue: (client) => client.customer_id || "",
  },
  {
    key: "primary_contact",
    label: "Primary Contact",
    getValue: (client) => client.customer_primary_contact || "",
  },
  {
    key: "email",
    label: "Email",
    getValue: (client) => client.customer_email || "",
  },
  {
    key: "vendor_name",
    label: "Vendor",
    getValue: (client) => client.vendor_name || "",
  },
  {
    key: "vendor_id",
    label: "Vendor ID",
    getValue: (client) => client.vendor_id || "",
  },
  {
    key: "transactions",
    label: "Transactions",
    getValue: (client) => client.journal_entries_count ?? 0,
  },
  {
    key: "total_amount",
    label: "Total Amount",
    getValue: (client) => client.total_amount ?? 0,
  },
];

const VENDOR_COPY_COLUMNS = [
  {
    key: "vendor_name",
    label: "Vendor Name",
    getValue: (vendor) => vendor.name || "",
  },
  {
    key: "vendor_id",
    label: "Vendor ID",
    getValue: (vendor) => vendor.id || "",
  },
  {
    key: "account",
    label: "Account",
    getValue: (vendor, accountLabelMap) =>
      accountLabelMap?.get?.(vendor.account) || "",
  },
  {
    key: "website",
    label: "Website",
    getValue: (vendor) => vendor.website || "",
  },
  {
    key: "description",
    label: "Description",
    getValue: (vendor) => vendor.description || "",
  },
];

const VENDOR_METRIC_COLUMNS = [
  {
    key: "journal_entries_total",
    label: "Total Amount Spent",
    getValue: (vendor) => vendor.journal_entries_total ?? 0,
  },
  {
    key: "customer_accounts_count",
    label: "Client Accounts",
    getValue: (vendor) => vendor.customer_accounts_count ?? 0,
  },
  {
    key: "journal_entries_count",
    label: "Transactions",
    getValue: (vendor) => vendor.journal_entries_count ?? 0,
  },
];

const buildVendorFilterParams = (filters, accountNumberLookup) => {
  if (!filters) return {};
  const params = {};

  const selectedAccountIds = (filters.accounts || [])
    .map((option) => option.value ?? option.id)
    .filter(Boolean);
  const hasAccountFilters = selectedAccountIds.length > 0;

  if (hasAccountFilters) {
    params.account = selectedAccountIds.join(",");
  }

  if (filters.matchJournalAccounts) {
    params.account_scope = hasAccountFilters ? "both" : "journal";
  } else if (hasAccountFilters) {
    params.account_scope = "vendor";
  }

  if (filters.customers?.length) {
    params.customer = filters.customers
      .map((option) => option.value ?? option.id)
      .filter(Boolean)
      .join(",");
  }

  if (filters.approvalStatus?.length) {
    params.approval_status = filters.approvalStatus
      .map((option) => option.value ?? option)
      .filter(Boolean)
      .join(",");
  }

  if (!filters.customers?.length && filters.accountTypes?.length) {
    const backendTypes = Array.from(
      new Set(
        filters.accountTypes
          .map((option) => option.value)
          .filter(Boolean)
          .map((value) => mapFrontendToBackend(value))
      )
    );
    if (backendTypes.length) {
      params.customer_type = backendTypes.join(",");
    }
  }

  if (filters.includeAccountChildren && hasAccountFilters) {
    const prefixSet = new Set();
    selectedAccountIds
      .map((id) => accountNumberLookup?.get(id))
      .filter(Boolean)
      .forEach((number) => {
        const digitsMatch = number.toString().match(/\d+/);
        if (!digitsMatch) {
          return;
        }
        const numeric = digitsMatch[0];
        if (!numeric) {
          return;
        }
        prefixSet.add(numeric);
        const trimmed = numeric.replace(/0+$/, "");
        if (trimmed && trimmed !== numeric) {
          prefixSet.add(trimmed);
        }
      });

    const prefixes = Array.from(prefixSet).filter(Boolean);
    if (prefixes.length) {
      params.account_prefix = prefixes.join(",");
    }
  }

  return params;
};

const getPreviousFullYearRange = () => {
  const now = new Date();
  const previousYear = now.getFullYear() - 1;
  const start = new Date(Date.UTC(previousYear, 0, 1));
  const end = new Date(Date.UTC(previousYear, 11, 31));
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
};

export const VendorsList = () => {
  const [vendorsList, setVendorsList] = useState([]);
  const [accountList, setAccountList] = useState([]);
  const accountsLoadPromiseRef = React.useRef(null);
  const [search, setSearch] = useState("");
  const hasSearchQuery = search.trim().length > 0;
  const [totalCount, setTotalCount] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [previousCursor, setPreviousCursor] = useState(null);
  const [fetchingVendors, setFetchingVendors] = useState(true);
  const [copying, setCopying] = useState(false);
  const context = React.useContext(Context);
  const fireToastRef = React.useRef(null);
  useEffect(() => {
    fireToastRef.current = context?.fireToast || null;
  }, [context?.fireToast]);

  const ensureAccountsLoaded = useCallback(() => {
    if (accountList?.length) {
      return Promise.resolve(accountList);
    }

    if (accountsLoadPromiseRef.current) {
      return accountsLoadPromiseRef.current;
    }

    const loadPromise = (async () => {
      try {
        const accounts = await fetchAccounts();
        const normalizedAccounts = (accounts || []).map((account) => {
          if (
            account &&
            "id" in account &&
            Object.prototype.hasOwnProperty.call(account, "name")
          ) {
            return account;
          }

          if (account?.value) {
            const [numberPart, ...nameParts] = (account.label || "").split(
              " - "
            );
            return {
              id: account.value,
              number: numberPart?.trim() || "",
              name: nameParts.join(" - ").trim() || "",
              label: account.label,
            };
          }

          return account;
        });

        setAccountList(normalizedAccounts);
        return normalizedAccounts;
      } catch (err) {
        console.error("Failed to load accounts", err);
        return [];
      } finally {
        accountsLoadPromiseRef.current = null;
      }
    })();

    accountsLoadPromiseRef.current = loadPromise;
    return loadPromise;
  }, [accountList]);

  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const onMergeOpen = useCallback(() => setIsMergeOpen(true), []);
  const onMergeClose = useCallback(() => setIsMergeOpen(false), []);

  const [isVendorOpen, setIsVendorOpen] = useState(false);
  const onVendorOpen = useCallback(() => setIsVendorOpen(true), []);
  const onVendorClose = useCallback(() => setIsVendorOpen(false), []);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const onFilterOpen = useCallback(() => {
    ensureAccountsLoaded();
    setIsFilterOpen(true);
  }, [ensureAccountsLoaded]);
  const onFilterClose = useCallback(() => setIsFilterOpen(false), []);

  const [isAccountsModalOpen, setIsAccountsModalOpen] = useState(false);
  const onAccountsModalOpen = useCallback(() => setIsAccountsModalOpen(true), []);
  const onAccountsModalClose = useCallback(() => setIsAccountsModalOpen(false), []);

  const [isPendingApprovalsOpen, setIsPendingApprovalsOpen] = useState(false);
  const onPendingApprovalsOpen = useCallback(() => setIsPendingApprovalsOpen(true), []);
  const onPendingApprovalsClose = useCallback(() => setIsPendingApprovalsOpen(false), []);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const onConfirmOpen = useCallback(() => setIsConfirmOpen(true), []);
  const onConfirmClose = useCallback(() => setIsConfirmOpen(false), []);
  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(getEmptyFilters());
  const [accountsModalVendor, setAccountsModalVendor] = useState(null);
  const [accountsModalData, setAccountsModalData] = useState(null);
  const [accountsModalLoading, setAccountsModalLoading] = useState(false);
  const [accountsModalError, setAccountsModalError] = useState(null);
  const [copyingAccounts, setCopyingAccounts] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [copyColumns, setCopyColumns] = useState(
    ACCOUNT_COPY_COLUMNS.map((column) => column.key)
  );
  const [vendorCopyModalOpen, setVendorCopyModalOpen] = useState(false);
  const [vendorCopyColumns, setVendorCopyColumns] = useState(
    VENDOR_COPY_COLUMNS.map((column) => column.key)
  );
  const [vendorCopyIncludeMetrics, setVendorCopyIncludeMetrics] = useState(false);
  const [vendorCopyMetricsStartDate, setVendorCopyMetricsStartDate] = useState("");
  const [vendorCopyMetricsEndDate, setVendorCopyMetricsEndDate] = useState("");
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingApprovalsLoading, setPendingApprovalsLoading] = useState(false);
  const [approvingVendorId, setApprovingVendorId] = useState(null);
  const [unapprovingVendorId, setUnapprovingVendorId] = useState(null);
  const muiTheme = useMemo(() => createTheme({ palette: { primary: { main: "#536DEC" } } }), []);
  const hasActiveFilters = useMemo(
    () =>
      appliedFilters.accounts.length > 0 ||
      appliedFilters.customers.length > 0 ||
      appliedFilters.accountTypes.length > 0 ||
      appliedFilters.approvalStatus.length > 0 ||
      appliedFilters.includeAccountChildren ||
      appliedFilters.matchJournalAccounts,
    [appliedFilters]
  );
  const activeFilterCount = useMemo(
    () =>
      appliedFilters.accounts.length +
      appliedFilters.customers.length +
      appliedFilters.accountTypes.length +
      appliedFilters.approvalStatus.length +
      (appliedFilters.includeAccountChildren ? 1 : 0) +
      (appliedFilters.matchJournalAccounts ? 1 : 0),
    [appliedFilters]
  );
  const accountOptions = useMemo(
    () =>
      accountList?.map((account) => {
        const value = account.id ?? account.value;
        const label =
          account.label ||
          `${account.number || ""} ${account.name || ""}`.trim();
        return {
          value,
          label,
        };
      }) || [],
    [accountList]
  );
  const accountLabelMap = useMemo(() => {
    const map = new Map();
    accountList.forEach((account) => {
      const id = account.id ?? account.value;
      if (!id) return;
      const label =
        account.label ||
        `${account.number || ""} ${account.name || ""}`.trim();
      map.set(id, label);
    });
    return map;
  }, [accountList]);

  const accountNumberMap = useMemo(() => {
    const map = new Map();
    accountList.forEach((account) => {
      const id = account.id ?? account.value;
      if (!id) return;
      let number = account.number;
      if (!number && account.label) {
        const [digits] = account.label.split(" - ");
        number = digits;
      }
      map.set(id, number || "");
    });
    return map;
  }, [accountList]);

  const needsAccountNumberLookup = Boolean(
    appliedFilters?.includeAccountChildren &&
      Array.isArray(appliedFilters?.accounts) &&
      appliedFilters.accounts.length
  );

  const accountNumberLookupForFilters = useMemo(() => {
    if (!needsAccountNumberLookup) {
      return null;
    }
    return accountNumberMap;
  }, [accountNumberMap, needsAccountNumberLookup]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(amount ?? 0));
  }, []);

  const buildQueryParams = useCallback(() => {
    const params = { limit: VENDORS_PAGE_SIZE };
    if (hasSearchQuery) {
      params.name__icontains = search;
    }
    return {
      ...params,
      ...buildVendorFilterParams(appliedFilters, accountNumberLookupForFilters),
    };
  }, [hasSearchQuery, search, appliedFilters, accountNumberLookupForFilters]);

  const refreshVendorsList = useCallback(
    (cursor) => {
      let params = buildQueryParams();
      if (cursor) {
        const urlObj = new URL(cursor);
        params = {
          ...params,
          cursor: urlObj.searchParams.get("cursor"),
        };
      }

      const fetchData = async () => {
        setFetchingVendors(true);
        try {
          const data = await fetchVendors(params);
          setVendorsList(data.results);
          setNextCursor(data.next);
          setPreviousCursor(data.previous);
          setTotalCount(
            typeof data.total_count === "number"
              ? data.total_count
              : data.results?.length || 0
          );
          setPendingApprovalCount(
            typeof data.pending_approvals_count === "number"
              ? data.pending_approvals_count
              : 0
          );
        } catch (err) {
          console.error("Failed to load vendors", err);
          fireToastRef.current &&
            fireToastRef.current(
              "Error",
              "Failed to load vendors. Please try again.",
              "error"
            );
        } finally {
          setFetchingVendors(false);
        }
      };
      fetchData();
    },
    [buildQueryParams]
  );

  const loadCustomerOptions = useCallback(
    async (inputValue = "", cursor = null) => {
      if (!context?.profile) return { options: [], next: null };

      const params = {
        is_active: true,
        page_size: 50,
      };

      const trimmed = (inputValue || "").trim();
      if (trimmed) {
        params.search = trimmed;
      }
      if (cursor) {
        params.cursor = cursor;
      }

      try {
        const data = await fetchCustomers(context.profile, params);
        const results = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];

        const options = results.map((customer) => ({
          value: customer.id ?? customer.value,
          label: customer.name ?? customer.label ?? "",
        }));

        return {
          options,
          next: typeof data?.next === "string" ? data.next : null,
        };
      } catch (err) {
        console.error("Failed to load customers", err);
        return { options: [], next: null };
      }
    },
    [context?.profile]
  );

  const fetchVendorAccountResults = useCallback(
    async (vendor) => {
      if (!vendor?.id) {
        setAccountsModalError("Vendor details are missing.");
        setAccountsModalData(null);
        return;
      }

      setAccountsModalLoading(true);
      setAccountsModalError(null);
      try {
        const data = await searchVendorAccounts({
          vendorId: vendor.id,
          vendorName: vendor.name,
        });
        setAccountsModalData(data);
      } catch (err) {
        console.error("Failed to load vendor accounts", err);
        setAccountsModalError(
          err?.response?.data?.details ||
            "Failed to load accounts for this vendor. Please try again."
        );
      } finally {
        setAccountsModalLoading(false);
      }
    },
    []
  );

  const handleOpenAccountsModal = useCallback(
    (vendor) => {
      if (!vendor) {
        return;
      }
      setAccountsModalVendor(vendor);
      setAccountsModalData(null);
      setAccountsModalError(null);
      onAccountsModalOpen();
      fetchVendorAccountResults(vendor);
    },
    [fetchVendorAccountResults, onAccountsModalOpen]
  );

  const handleCloseAccountsModal = useCallback(() => {
    onAccountsModalClose();
    setAccountsModalVendor(null);
    setAccountsModalData(null);
    setAccountsModalError(null);
    setCopyingAccounts(false);
    setIsCopyModalOpen(false);
    setCopyColumns(ACCOUNT_COPY_COLUMNS.map((column) => column.key));
  }, [onAccountsModalClose]);

  const handleOpenCopyModal = useCallback(() => {
    if (!accountsModalData?.clients?.length) {
      context?.fireToast &&
        context.fireToast(
          "No Accounts",
          "There are no account results to copy.",
          "warning"
        );
      return;
    }
    setCopyColumns(ACCOUNT_COPY_COLUMNS.map((column) => column.key));
    setIsCopyModalOpen(true);
  }, [accountsModalData?.clients?.length, context]);

  const sanitizeForClipboard = useCallback((value) => {
    if (value === null || value === undefined) {
      return "";
    }
    return value
      .toString()
      .replace(/\t/g, " ")
      .replace(/\r?\n|\r/g, " ");
  }, []);

  const handleConfirmCopyAccounts = useCallback(async () => {
    if (!accountsModalData?.clients?.length) {
      context?.fireToast &&
        context.fireToast(
          "No Accounts",
          "There are no account results to copy.",
          "warning"
        );
      return;
    }

    if (!navigator?.clipboard?.writeText) {
      context?.fireToast &&
        context.fireToast(
          "Clipboard Unavailable",
          "Copying is not supported in this browser.",
          "error"
        );
      return;
    }

    const selectedColumns = ACCOUNT_COPY_COLUMNS.filter((column) =>
      copyColumns.includes(column.key)
    );

    if (!selectedColumns.length) {
      context?.fireToast &&
        context.fireToast(
          "No Columns Selected",
          "Select at least one column to include in the copy.",
          "warning"
        );
      return;
    }

    setCopyingAccounts(true);
    try {
      const lines = [];
      lines.push(
        selectedColumns.map((column) => column.label).join("\t")
      );

      accountsModalData.clients.forEach((client) => {
        const values = selectedColumns.map((column) => {
          if (column.key === "total_amount") {
            return sanitizeForClipboard(
              formatCurrency(column.getValue(client))
            );
          }
          return sanitizeForClipboard(column.getValue(client));
        });
        lines.push(values.join("\t"));
      });

      const clipboardText = lines.join("\n");
      await navigator.clipboard.writeText(clipboardText);

      context?.fireToast &&
        context.fireToast(
          "Copied",
          `${accountsModalData.clients.length} account${
            accountsModalData.clients.length === 1 ? "" : "s"
          } copied to clipboard`,
          "success"
        );
    } catch (err) {
      console.error("Failed to copy account results", err);
      context?.fireToast &&
        context.fireToast(
          "Copy Failed",
          "Unable to copy account results. Please try again.",
          "error"
        );
    } finally {
      setCopyingAccounts(false);
      setIsCopyModalOpen(false);
    }
  }, [
    accountsModalData,
    context,
    copyColumns,
    formatCurrency,
    sanitizeForClipboard,
  ]);

  const handleCloseCopyModal = useCallback(() => {
    setIsCopyModalOpen(false);
    setCopyingAccounts(false);
  }, []);

  const handleRetryAccounts = useCallback(() => {
    if (accountsModalVendor) {
      fetchVendorAccountResults(accountsModalVendor);
    }
  }, [accountsModalVendor, fetchVendorAccountResults]);

  const removeVendorFromList = useCallback(
    (vendorId) => {
      setVendorsList((prevList) =>
        prevList.filter((vendor) => vendor.id !== vendorId)
      );
    },
    []
  );

  useEffect(() => {
    refreshVendorsList();
  }, [refreshVendorsList]);

  // Do not preload accounts or customers on page load.
  // Accounts are loaded lazily when the user opens filters/vendor modal/export.

  const handleNextPage = () => {
    if (nextCursor) {
      refreshVendorsList(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    if (previousCursor) {
      refreshVendorsList(previousCursor);
    }
  };

  const handleMergeSubmit = (data) => {
    setLoading(true);
    mergeVendors(data)
      .then((response) => {
        setLoading(false);
        if (response.status === 200) {
          onMergeClose();
          context.fireToast(
            "Success",
            "Successfully merged the vendors",
            "success"
          );
          setVendorsList((prevList) =>
            prevList.filter(
              (vendor) => data.duplicates.indexOf(vendor.id) === -1
            )
          );
        } else {
          context.fireToast(
            "Error",
            "There was an error merging the vendors.",
            "error"
          );
          onMergeClose();
        }
      })
      .catch((error) => {
        setLoading(false);
        context.fireToast(
          "Error",
          "There was an error merging the vendors!",
          "error"
        );
        console.error("There was an error merging the vendors!", error);
      });
  };

  const performConfirmAction = useCallback(async () => {
    const vendorId = confirmConfig?.vendor?.id;
    if (!vendorId) {
      onConfirmClose();
      return;
    }
    setConfirmLoading(true);

    if (confirmConfig.mode === "delete") {
      try {
        const response = await deleteVendor(vendorId);
        if (response?.status === 204) {
          removeVendorFromList(vendorId);
          context?.fireToast &&
            context.fireToast(
              "Deleted",
              `${confirmConfig.vendor?.name || "Vendor"} deleted.`,
              "success"
            );
        } else {
          context?.fireToast &&
            context.fireToast(
              "Error",
              response?.request?.responseText || "Delete failed",
              "error"
            );
        }
      } catch (err) {
        console.error("Failed to delete vendor", err);
        context?.fireToast &&
          context.fireToast("Error", "Failed to delete the vendor.", "error");
      } finally {
        setConfirmLoading(false);
        setConfirmConfig(null);
        onConfirmClose();
      }
      return;
    }

    if (confirmConfig.mode === "approve") {
      setApprovingVendorId(vendorId);
      try {
        const data = await approveVendor(vendorId);
        setVendorsList((prevList) =>
          prevList.map((item) =>
            item.id === data.id ? { ...item, ...data } : item
          )
        );
        setPendingApprovals((prev) =>
          prev.filter(
            (candidate) => candidate?.suggested_vendor?.id !== vendorId
          )
        );
        setPendingApprovalCount((count) => (count > 0 ? count - 1 : 0));
        context?.fireToast &&
          context.fireToast(
            "Approved",
            `${data.name || "Vendor"} approved successfully.`,
            "success"
          );
      } catch (err) {
        console.error("Failed to approve vendor", err);
        context?.fireToast &&
          context.fireToast(
            "Error",
            "Failed to approve vendor. Please try again.",
            "error"
          );
      } finally {
        setApprovingVendorId(null);
        setConfirmLoading(false);
        setConfirmConfig(null);
        onConfirmClose();
      }
    }
  }, [confirmConfig, context, onConfirmClose, removeVendorFromList, setVendorsList]);

  const handleUnapproveVendor = useCallback(
    async (vendor) => {
      if (!vendor?.id) {
        return;
      }
      setUnapprovingVendorId(vendor.id);
      try {
        const data = await unapproveVendor(vendor.id);
        setVendorsList((prevList) =>
          prevList.map((item) =>
            item.id === data.id ? { ...item, ...data } : item
          )
        );
        context?.fireToast &&
          context.fireToast(
            "Updated",
            `${data.name || "Vendor"} marked as pending approval.`,
            "info"
          );
      } catch (err) {
        console.error("Failed to unapprove vendor", err);
        context?.fireToast &&
          context.fireToast(
            "Error",
            "Failed to unapprove vendor. Please try again.",
            "error"
          );
      } finally {
        setUnapprovingVendorId(null);
      }
    },
    [context]
  );

  const startDeleteVendor = (vendor) => {
    setSelectedVendor(vendor);
    setConfirmConfig({ mode: "delete", vendor });
    onConfirmOpen();
  };

  const startApproveVendor = useCallback(
    (vendor) => {
      if (!vendor) return;
      setConfirmConfig({ mode: "approve", vendor });
      onConfirmOpen();
    },
    [onConfirmOpen]
  );

  const startCreateVendor = async () => {
    await ensureAccountsLoaded();
    setSelectedVendor(null);
    onVendorOpen();
  };

  const startUpdateVendor = async (vendor) => {
    await ensureAccountsLoaded();
    setSelectedVendor(vendor);
    onVendorOpen();
  };

  const handleOpenPendingApprovals = useCallback(async () => {
    onPendingApprovalsOpen();
    setPendingApprovalsLoading(true);
    try {
      const data = await fetchPendingVendorApprovals();
      setPendingApprovals(data.results || []);
    } catch (err) {
      console.error("Failed to load pending approvals", err);
      context?.fireToast &&
        context.fireToast(
          "Error",
          "Failed to load pending approvals. Please try again.",
          "error"
        );
    } finally {
      setPendingApprovalsLoading(false);
    }
  }, [context, onPendingApprovalsOpen]);

  const handleClosePendingApprovals = useCallback(() => {
    onPendingApprovalsClose();
  }, [onPendingApprovalsClose]);

  const getAccountName = (vendor) =>
    accountLabelMap.get(vendor.account) || "";

  const handleApplyFilters = (filters) => {
    setAppliedFilters({
      accounts: [...(filters.accounts || [])],
      customers: [...(filters.customers || [])],
      accountTypes: [...(filters.accountTypes || [])],
      approvalStatus: [...(filters.approvalStatus || [])],
      includeAccountChildren: Boolean(filters.includeAccountChildren),
      matchJournalAccounts: Boolean(filters.matchJournalAccounts),
    });
    onFilterClose();
  };

  const handleClearFilters = () => {
    const empty = getEmptyFilters();
    setAppliedFilters(empty);
  };

  const handleOpenVendorCopyModal = useCallback(async () => {
    await ensureAccountsLoaded();
    const { startDate, endDate } = getPreviousFullYearRange();
    setVendorCopyColumns(VENDOR_COPY_COLUMNS.map((column) => column.key));
    setVendorCopyIncludeMetrics(false);
    setVendorCopyMetricsStartDate(startDate);
    setVendorCopyMetricsEndDate(endDate);
    setVendorCopyModalOpen(true);
  }, [ensureAccountsLoaded]);

  const handleCloseVendorCopyModal = useCallback(() => {
    if (copying) {
      return;
    }
    setVendorCopyModalOpen(false);
    setVendorCopyColumns(VENDOR_COPY_COLUMNS.map((column) => column.key));
    setVendorCopyIncludeMetrics(false);
    setVendorCopyMetricsStartDate("");
    setVendorCopyMetricsEndDate("");
  }, [copying]);

  const handleCopyVendors = useCallback(
    async () => {
      const selectedColumns = VENDOR_COPY_COLUMNS.filter((column) =>
        vendorCopyColumns.includes(column.key)
      );
      const metricColumns = vendorCopyIncludeMetrics
        ? VENDOR_METRIC_COLUMNS
        : [];
      const exportColumns = [...selectedColumns, ...metricColumns];

      if (!exportColumns.length) {
        context?.fireToast &&
          context.fireToast(
            "No Columns Selected",
            "Select at least one column to include in the export.",
            "warning"
          );
        return;
      }

      setCopying(true);
      try {
        if (vendorCopyIncludeMetrics) {
          if (!vendorCopyMetricsStartDate || !vendorCopyMetricsEndDate) {
            context?.fireToast &&
              context.fireToast(
                "Missing Date Range",
                "Select both a start and end date for vendor metrics.",
                "warning"
              );
            return;
          }
        }

        const queryParams = buildQueryParams();
        const payload = {
          query_params: queryParams,
          columns: exportColumns.map((column) => column.key),
          include_summary: vendorCopyIncludeMetrics,
          metrics_start_date:
            vendorCopyIncludeMetrics ? vendorCopyMetricsStartDate || null : null,
          metrics_end_date:
            vendorCopyIncludeMetrics ? vendorCopyMetricsEndDate || null : null,
        };

        const result = await createVendorsExportLink(payload);
        const downloadUrl = result?.download_url;
        if (!downloadUrl) {
          throw new Error("Missing download_url");
        }

        window.location.href = downloadUrl;

        context?.fireToast &&
          context.fireToast(
            "Export Started",
            "Your vendor export is downloading.",
            "success"
          );
        setVendorCopyModalOpen(false);
      } catch (err) {
        console.error("Unable to export vendor list", err);
        context?.fireToast &&
          context.fireToast(
            "Export Failed",
            "Unable to start vendor export. Please try again.",
            "error"
          );
      } finally {
        setCopying(false);
      }
    },
    [
      buildQueryParams,
      context,
      vendorCopyColumns,
      vendorCopyIncludeMetrics,
      vendorCopyMetricsStartDate,
      vendorCopyMetricsEndDate,
    ]
  );

  const handleSubmit = useCallback(
    async (event, data) => {
      event.preventDefault();
      setLoading(true);

      const showToast = (title, description, status) => {
        if (context?.fireToast) {
          context.fireToast(title, description, status);
        }
      };

      try {
        if (data.id) {
          const res = await updateVendor(data.id, data);
          const updatedVendor = res?.data || data;

          setVendorsList((prevList) =>
            prevList.map((v) => (v.id === updatedVendor.id ? updatedVendor : v))
          );
          showToast("Success", "Vendor updated.", "success");
        } else {
          const res = await createVendor(data);
          const newVendor = res?.data || data;
          setVendorsList((prevList) => [...prevList, newVendor]);
          showToast("Success", "Vendor created.", "success");
        }

        onVendorClose();
      } catch (err) {
        console.error("Failed to submit vendor", err);
        showToast(
          "Error",
          "Failed to save vendor. Please try again.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    },
    [context, onVendorClose]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <MuiContainer
        maxWidth={false}
        sx={{
          minHeight: "100vh",
          bgcolor: "#eef3ff",
          py: 6,
          px: 4,
          pt: 12,
          width: "100%",
        }}
      >
        <MuiStack spacing={2}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(83, 109, 236, 0.08)",
              bgcolor: "#f9fbff",
            }}
          >
          <MuiStack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ pb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              Vendor Listing
            </Typography>
            <MuiStack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
              <MuiButton
                startIcon={<CheckCircle size={16} />}
                onClick={handleOpenPendingApprovals}
                variant={pendingApprovalCount ? "contained" : "outlined"}
                color={pendingApprovalCount ? "primary" : "inherit"}
                sx={{ textTransform: "none", minWidth: 110 }}
              >
                Pending{pendingApprovalCount ? ` (${pendingApprovalCount})` : ""}
              </MuiButton>
              <MuiButton
                startIcon={<GitMerge />}
                onClick={onMergeOpen}
                variant="outlined"
                sx={{ textTransform: "none" }}
              >
                Merge
              </MuiButton>
              <MergeVendorsModal
                isOpen={isMergeOpen}
                onClose={onMergeClose}
                loading={loading}
                handleSubmit={handleMergeSubmit}
              />
              <VendorConfirmationModal
                isOpen={isConfirmOpen}
                onClose={() => {
                  setConfirmConfig(null);
                  onConfirmClose();
                }}
                title={
                  confirmConfig?.mode === "delete"
                    ? "Confirm Delete"
                    : "Approve Vendor"
                }
                message={
                  confirmConfig?.mode === "delete"
                    ? `Are you sure you want to delete ${
                        confirmConfig?.vendor?.name || "this vendor"
                      }?`
                    : `Are you sure you want to approve ${
                        confirmConfig?.vendor?.name || "this vendor"
                      }?`
                }
                confirmLabel={
                  confirmConfig?.mode === "delete" ? "Delete" : "Approve"
                }
                confirmColor={
                  confirmConfig?.mode === "delete" ? "error" : "success"
                }
                loading={confirmLoading}
                onConfirm={performConfirmAction}
              />
              <VendorModal
                isOpen={isVendorOpen}
                onClose={onVendorClose}
                vendor={selectedVendor}
                accountList={accountList}
                handleSubmit={handleSubmit}
                loading={loading}
              />
              <FilterVendorsModal
                isOpen={isFilterOpen}
                onClose={onFilterClose}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
                initialFilters={appliedFilters}
                accountOptions={accountOptions}
                loadCustomerOptions={loadCustomerOptions}
              />
              <MuiButton
                startIcon={<Filter size={16} />}
                onClick={onFilterOpen}
                variant={hasActiveFilters ? "contained" : "outlined"}
                color={hasActiveFilters ? "primary" : "inherit"}
                sx={{ textTransform: "none", minWidth: 90 }}
              >
                Filters{hasActiveFilters ? ` (${activeFilterCount})` : ""}
              </MuiButton>
              <MuiButton
                startIcon={<AddIcon />}
                onClick={startCreateVendor}
                variant="contained"
                sx={{ textTransform: "none" }}
              >
                Create Vendor
              </MuiButton>
              <TextField
                size="small"
                placeholder="Search..."
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 280, bgcolor: "white", borderRadius: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </MuiStack>
          </MuiStack>
          <MuiStack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              Vendor Results{hasActiveFilters || hasSearchQuery ? " (filtered)" : ""}
            </Typography>
            <MuiStack direction="row" spacing={3} alignItems="center">
              <Typography variant="body2">
                Showing <strong>{vendorsList.length}</strong> of <strong>{totalCount}</strong> vendors
              </Typography>
              {(hasActiveFilters || hasSearchQuery) && (
                <Typography variant="body2">
                  <strong>{activeFilterCount + (hasSearchQuery ? 1 : 0)}</strong>{" "}
                  {activeFilterCount + (hasSearchQuery ? 1 : 0) === 1 ? "filter" : "filters"} applied
                </Typography>
              )}
              <MuiIconButton
                aria-label="Download vendors CSV"
                onClick={handleOpenVendorCopyModal}
                disabled={copying || fetchingVendors || !totalCount}
                size="small"
              >
                <Download size={16} />
              </MuiIconButton>
            </MuiStack>
          </MuiStack>
          {fetchingVendors ? (
            <MuiBox display="flex" justifyContent="center" minHeight="300px" alignItems="center">
              <CircularProgress />
            </MuiBox>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                height: "79vh",
                maxHeight: "none",
                overflowX: "auto",
                overflowY: "auto",
                borderRadius: 2,
                border: "1px solid #e1e8ff",
              }}
            >
              <MuiTable stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {["Name", "Account", "Website", "Approved", "Action"].map((header) => (
                      <TableCell
                        key={header}
                        align={header === "Action" ? "right" : "left"}
                        sx={{
                          backgroundColor: "#E8EEFF",
                          color: "#4258C9",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          fontSize: 12,
                          letterSpacing: 0.4,
                          borderBottom: "1px solid #D6E0FF",
                          width: header === "Action" ? 260 : "auto",
                          whiteSpace: header === "Action" ? "nowrap" : "normal",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendorsList.map((vendor) => (
                    <TableRow
                      key={vendor.id}
                      hover
                      sx={{
                        height: 48,
                        "& > td": {
                          verticalAlign: "middle",
                          paddingTop: "8px",
                          paddingBottom: "8px",
                        },
                      }}
                    >
                      <TableCell sx={{ verticalAlign: "middle" }}>
                        <MuiBox display="flex" alignItems="center" height="100%" lineHeight={1.4}>
                          <Avatar
                            src={
                              vendor.logo ||
                              `https://ui-avatars.com/api/?size=24&name=${vendor.name}`
                            }
                            alt={vendor.name || "Placeholder"}
                            sx={{ width: 25, height: 25, mr: 1.5 }}
                          />
                          {vendor.name}
                        </MuiBox>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "middle" }}>
                        <MuiBox display="flex" alignItems="center" height="100%" lineHeight={1.4}>
                          {getAccountName(vendor)}
                        </MuiBox>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "middle" }}>
                        <MuiBox display="flex" alignItems="center" height="100%" lineHeight={1.4}>
                          {vendor.website}
                        </MuiBox>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "middle" }}>
                        <MuiBox display="flex" alignItems="center" height="100%" lineHeight={1.4}>
                          <Chip
                            label={
                              vendor.is_approved
                                ? "YES"
                                : vendor.has_pending_candidates
                                ? "PENDING"
                                : "NO"
                            }
                            color={
                              vendor.is_approved
                                ? "success"
                                : vendor.has_pending_candidates
                                ? "warning"
                                : "default"
                            }
                            size="small"
                            variant="outlined"
                          />
                        </MuiBox>
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: "middle" }}>
                        <MuiStack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ height: "100%" }}>
                          <MuiButton
                            variant="outlined"
                            size="small"
                            color="primary"
                            sx={{ minWidth: 80, fontWeight: 600 }}
                            onClick={() => handleOpenAccountsModal(vendor)}
                          >
                            Accounts
                          </MuiButton>
                          {!vendor.is_approved ? (
                            <MuiButton
                              variant="outlined"
                              size="small"
                              color="success"
                              sx={{ minWidth: 82, fontWeight: 600, borderColor: "#60c08b", color: "#2e8b57" }}
                              onClick={() => startApproveVendor(vendor)}
                              disabled={approvingVendorId === vendor.id}
                            >
                              {approvingVendorId === vendor.id ? "Approving..." : "Approve"}
                            </MuiButton>
                          ) : (
                            <MuiButton
                              variant="outlined"
                              size="small"
                              color="warning"
                              sx={{ minWidth: 96, fontWeight: 600 }}
                              onClick={() => handleUnapproveVendor(vendor)}
                              disabled={unapprovingVendorId === vendor.id}
                            >
                              {unapprovingVendorId === vendor.id ? "Updating..." : "Unapprove"}
                            </MuiButton>
                          )}
                          <MuiButton
                            variant="contained"
                            size="small"
                            color="primary"
                            sx={{ minWidth: 80, fontWeight: 600, bgcolor: "#536DEC" }}
                            onClick={() => startUpdateVendor(vendor)}
                          >
                            Update
                          </MuiButton>
                          <MuiButton
                            variant="contained"
                            size="small"
                            color="error"
                            sx={{ minWidth: 72, fontWeight: 600 }}
                            onClick={() => startDeleteVendor(vendor)}
                          >
                            Delete
                          </MuiButton>
                        </MuiStack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </MuiTable>
            </TableContainer>
          )}
          <MuiStack direction="row" spacing={2} mt={3} justifyContent="center">
            {previousCursor && (
              <MuiButton
                onClick={handlePreviousPage}
                disabled={fetchingVendors || !previousCursor}
                variant="outlined"
              >
                Previous
              </MuiButton>
            )}
            {nextCursor && (
              <MuiButton
                onClick={handleNextPage}
                disabled={fetchingVendors || !nextCursor}
                variant="outlined"
              >
                Next
              </MuiButton>
            )}
          </MuiStack>
        </Paper>
      </MuiStack>
      <VendorAccountsModal
        isOpen={isAccountsModalOpen}
        onClose={handleCloseAccountsModal}
        vendor={accountsModalVendor}
        loading={accountsModalLoading}
        data={accountsModalData}
        error={accountsModalError}
        onOpenCopyModal={handleOpenCopyModal}
        formatCurrency={formatCurrency}
        onRetry={handleRetryAccounts}
      />
      <VendorsExportModal
        isOpen={isCopyModalOpen}
        onClose={handleCloseCopyModal}
        columns={ACCOUNT_COPY_COLUMNS}
        selectedColumns={copyColumns}
        onChangeColumns={setCopyColumns}
        onCopy={handleConfirmCopyAccounts}
        copying={copyingAccounts}
        includeVendorSummary={false}
        onToggleVendorSummary={() => {}}
        isLargeExport={false}
      />
      <PendingApprovalsModal
        isOpen={isPendingApprovalsOpen}
        onClose={handleClosePendingApprovals}
        approvals={pendingApprovals}
        loading={pendingApprovalsLoading}
        onApprove={startApproveVendor}
        approvingVendorId={approvingVendorId}
      />
      <VendorsExportModal
        isOpen={vendorCopyModalOpen}
        onClose={handleCloseVendorCopyModal}
        columns={VENDOR_COPY_COLUMNS}
        selectedColumns={vendorCopyColumns}
        onChangeColumns={setVendorCopyColumns}
        includeVendorSummary={vendorCopyIncludeMetrics}
        onToggleVendorSummary={setVendorCopyIncludeMetrics}
        showMetricsDateRange
        metricsStartDate={vendorCopyMetricsStartDate}
        metricsEndDate={vendorCopyMetricsEndDate}
        onChangeMetricsStartDate={setVendorCopyMetricsStartDate}
        onChangeMetricsEndDate={setVendorCopyMetricsEndDate}
        onCopy={handleCopyVendors}
        copying={copying}
        alwaysDownloadCsv
      />
      </MuiContainer>
    </ThemeProvider>
  );
};
